import BehanceIcon from '@icons/platforms/BehanceIcon';
import BlogIcon from '@icons/platforms/BlogIcon';
import FacebookIcon from '@icons/platforms/FacebookIcon';
import GithubIcon from '@icons/platforms/GithubIcon';
import InstagramIcon from '@icons/platforms/InstagramIcon';
import LinkedInIcon from '@icons/platforms/LinkedInIcon';
import PinterestIcon from '@icons/platforms/PinterestIcon';
import SnapchatIcon from '@icons/platforms/SnapchatIcon';
import SoundCloudIcon from '@icons/platforms/SoundCloudIcon';
import TikTokIcon from '@icons/platforms/TikTokIcon';
import VimeoIcon from '@icons/platforms/VimeoIcon';
import WebsiteIcon from '@icons/platforms/WebsiteIcon';
import XTwitterIcon from '@icons/platforms/XTwitterIcon';
import YouTubeIcon from '@icons/platforms/YouTubeIcon';
import { AccountProfileLinkType } from '@models/accounts';
import { ReactNode } from 'react';

type PlatformIcon = (props: { size?: number; className?: string }) => ReactNode;

interface SocialPlatform {
  type: AccountProfileLinkType;
  Icon: PlatformIcon;
  exampleUrl: string;
}

// Single source of truth for platform → icon + example URL. Display order is popularity-ish.
export const SOCIAL_PLATFORMS: SocialPlatform[] = [
  {
    type: AccountProfileLinkType.INSTAGRAM,
    Icon: InstagramIcon,
    exampleUrl: 'https://instagram.com/username'
  },
  {
    type: AccountProfileLinkType.FACEBOOK,
    Icon: FacebookIcon,
    exampleUrl: 'https://facebook.com/username'
  },
  { type: AccountProfileLinkType.X, Icon: XTwitterIcon, exampleUrl: 'https://x.com/username' },
  {
    type: AccountProfileLinkType.LINKEDIN,
    Icon: LinkedInIcon,
    exampleUrl: 'https://linkedin.com/in/username'
  },
  {
    type: AccountProfileLinkType.YOUTUBE,
    Icon: YouTubeIcon,
    exampleUrl: 'https://youtube.com/@username'
  },
  {
    type: AccountProfileLinkType.TIKTOK,
    Icon: TikTokIcon,
    exampleUrl: 'https://tiktok.com/@username'
  },
  {
    type: AccountProfileLinkType.PINTEREST,
    Icon: PinterestIcon,
    exampleUrl: 'https://pinterest.com/username'
  },
  {
    type: AccountProfileLinkType.SNAPCHAT,
    Icon: SnapchatIcon,
    exampleUrl: 'https://snapchat.com/add/username'
  },
  {
    type: AccountProfileLinkType.BEHANCE,
    Icon: BehanceIcon,
    exampleUrl: 'https://behance.net/username'
  },
  {
    type: AccountProfileLinkType.GITHUB,
    Icon: GithubIcon,
    exampleUrl: 'https://github.com/username'
  },
  { type: AccountProfileLinkType.VIMEO, Icon: VimeoIcon, exampleUrl: 'https://vimeo.com/username' },
  {
    type: AccountProfileLinkType.SOUNDCLOUD,
    Icon: SoundCloudIcon,
    exampleUrl: 'https://soundcloud.com/username'
  },
  { type: AccountProfileLinkType.BLOG, Icon: BlogIcon, exampleUrl: 'https://yourblog.com' },
  { type: AccountProfileLinkType.WEBSITE, Icon: WebsiteIcon, exampleUrl: 'https://yourwebsite.com' }
];

const PLATFORM_BY_TYPE = SOCIAL_PLATFORMS.reduce(
  (acc, p) => {
    acc[p.type] = p;
    return acc;
  },
  {} as Record<AccountProfileLinkType, SocialPlatform>
);

export function getSocialPlatform(type: AccountProfileLinkType): SocialPlatform {
  return PLATFORM_BY_TYPE[type] ?? PLATFORM_BY_TYPE[AccountProfileLinkType.WEBSITE];
}

export function getSocialPlatformIcon(type: AccountProfileLinkType, size = 32): ReactNode {
  const Icon = getSocialPlatform(type).Icon;
  return <Icon size={size} />;
}

export function normalizeSocialUrl(raw: string | null | undefined): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}
